import React, { useMemo, useState } from 'react';
import { Shield, ShieldCheck, Search, AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import { agruparPermisos, alternarPermiso, ETIQUETA_FEATURE } from './permisosPuntoV';

const claseInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500';

const normalizar = (t) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Crear o editar un rol de un cliente de Punto V: datos, permisos y máquinas.
// Al guardar se reemplazan todos sus permisos y máquinas.
const FormularioRolPuntoV = ({ cliente, rol, permisos, maquinas, onGuardar, onCancel }) => {
  const editando = Boolean(rol);
  const [form, setForm] = useState({
    nombre: rol?.nombre || '',
    descripcion: rol?.descripcion || '',
    activo: rol ? rol.activo : true
  });

  const idPorCodigo = useMemo(() => new Map(permisos.map(p => [p.codigo, p.id])), [permisos]);
  const codigoPorId = useMemo(() => new Map(permisos.map(p => [p.id, p.codigo])), [permisos]);
  const disponibles = useMemo(() => new Set(permisos.map(p => p.codigo)), [permisos]);
  const grupos = useMemo(() => agruparPermisos(permisos), [permisos]);

  const [seleccion, setSeleccion] = useState(
    () => new Set((rol?.permisos || []).map(id => codigoPorId.get(id)).filter(Boolean))
  );
  // Permisos del rol que aquí no se ofrecen (función apagada para el cliente o
  // retirados del catálogo). Punto V los conserva al guardar.
  const permisosNoElegibles = (rol?.permisos || []).filter(id => !codigoPorId.has(id)).length;

  const idsMaquinas = useMemo(() => new Set(maquinas.map(m => String(m.id_maquina))), [maquinas]);
  const [maquinasRol, setMaquinasRol] = useState(() => new Set((rol?.maquinas || []).map(String)));
  const maquinasAjenas = [...maquinasRol].filter(m => !idsMaquinas.has(m));

  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const q = normalizar(busqueda.trim());
  const gruposVisibles = q
    ? grupos
      .map(g => ({ ...g, permisos: g.permisos.filter(p => normalizar(`${p.nombre} ${p.codigo} ${p.descripcion || ''}`).includes(q)) }))
      .filter(g => g.permisos.length)
    : grupos;

  const alternar = (codigo, marcar) => setSeleccion(prev => alternarPermiso(prev, codigo, marcar, disponibles));

  const alternarGrupo = (grupo, marcar) => setSeleccion(prev => {
    let siguiente = new Set(prev);
    grupo.permisos.forEach(p => { siguiente = alternarPermiso(siguiente, p.codigo, marcar, disponibles); });
    return siguiente;
  });

  const alternarMaquina = (id) => setMaquinasRol(prev => {
    const siguiente = new Set(prev);
    if (siguiente.has(id)) siguiente.delete(id); else siguiente.add(id);
    return siguiente;
  });

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      await onGuardar({
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        activo: form.activo,
        permisos: [...seleccion].map(c => idPorCodigo.get(c)).filter(Boolean),
        maquinas: [...maquinasRol].filter(m => idsMaquinas.has(m))
      });
    } catch (err) {
      setError(err.message || 'No se pudo guardar el rol');
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={editando ? `Editar rol ${rol.nombre}` : 'Nuevo rol'}
      subtitulo={cliente.base_datos}
      icono={editando ? ShieldCheck : Shield}
      onClose={onCancel}
      bloqueado={guardando}
      ancho="xl"
      pie={(
        <>
          <span className="mr-auto self-center text-xs text-gray-500">
            {seleccion.size} permiso(s) · {[...maquinasRol].filter(m => idsMaquinas.has(m)).length} máquina(s)
          </span>
          <button type="button" onClick={onCancel} disabled={guardando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="submit"
            form="form-rol-puntov"
            disabled={guardando}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear rol'}
          </button>
        </>
      )}
    >
      <form id="form-rol-puntov" onSubmit={enviar} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="rol-puntov-nombre" className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              id="rol-puntov-nombre"
              value={form.nombre}
              onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
              required
              minLength={2}
              maxLength={250}
              placeholder="Ej. Recargador"
              className={claseInput}
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="rol-puntov-descripcion" className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
            <input
              id="rol-puntov-descripcion"
              value={form.descripcion}
              onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
              maxLength={255}
              placeholder="Qué hace quien tiene este rol"
              className={claseInput}
            />
          </div>
        </div>

        <label htmlFor="rol-puntov-activo" className={`flex items-center gap-2 text-sm text-gray-700 ${rol?.es_del_dueno ? 'opacity-60' : 'cursor-pointer'}`}>
          <input
            id="rol-puntov-activo"
            type="checkbox"
            checked={form.activo}
            onChange={(e) => setForm(prev => ({ ...prev, activo: e.target.checked }))}
            disabled={rol?.es_del_dueno}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Rol activo
          {rol?.es_del_dueno && <span className="text-xs text-gray-400">(es el rol del dueño: siempre activo)</span>}
        </label>

        {permisosNoElegibles > 0 && (
          <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
            Este rol tiene además {permisosNoElegibles} permiso(s) que aquí no se muestran: de funciones que este
            cliente tiene apagadas (por ejemplo Tránsito) o retirados del catálogo. Se conservan al guardar.
          </p>
        )}
        {maquinasAjenas.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Al guardar se quitarán las máquinas que ya no existen: {maquinasAjenas.join(', ')}.</span>
          </div>
        )}

        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-800">Permisos</h4>
            <div className="relative w-full sm:w-64">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                id="rol-puntov-busqueda"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar permiso"
                aria-label="Buscar permiso"
                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Al marcar un permiso se marcan también los que necesita; al desmarcarlo se quitan los que dependen de él.
          </p>
          {gruposVisibles.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Ningún permiso coincide con la búsqueda</p>}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {gruposVisibles.map(g => {
              const marcados = g.permisos.filter(p => seleccion.has(p.codigo)).length;
              const todos = marcados === g.permisos.length;
              return (
                <fieldset key={g.grupo} className="rounded-xl border border-gray-200 min-w-0">
                  <legend className="sr-only">{g.grupo}</legend>
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-xl">
                    <span className="text-sm font-medium text-gray-800">{g.grupo}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 tabular-nums">{marcados}/{g.permisos.length}</span>
                      <button
                        type="button"
                        onClick={() => alternarGrupo(g, !todos)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {todos ? 'Quitar todos' : 'Marcar todos'}
                      </button>
                    </span>
                  </div>
                  <ul className="p-2 space-y-0.5">
                    {g.permisos.map(p => (
                      <li key={p.id} style={{ paddingLeft: `${(q ? 0 : p.nivel) * 1.25}rem` }}>
                        <label htmlFor={`permiso-${p.id}`} className="flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                          <input
                            id={`permiso-${p.id}`}
                            type="checkbox"
                            checked={seleccion.has(p.codigo)}
                            onChange={(e) => alternar(p.codigo, e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="min-w-0">
                            <span className="text-sm text-gray-800">{p.nombre}</span>
                            {p.feature && (
                              <span className="ml-1.5 inline-block px-1.5 rounded bg-violet-50 text-violet-700 text-[10px] font-medium align-middle">
                                {ETIQUETA_FEATURE[p.feature] || p.feature}
                              </span>
                            )}
                            {p.descripcion && <span className="block text-xs text-gray-500">{p.descripcion}</span>}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
              );
            })}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-800">Máquinas que ve este rol</h4>
            {maquinas.length > 0 && (
              <span className="flex gap-3 text-xs">
                <button type="button" onClick={() => setMaquinasRol(new Set(idsMaquinas))} className="text-blue-600 hover:underline">Todas</button>
                <button type="button" onClick={() => setMaquinasRol(new Set())} className="text-blue-600 hover:underline">Ninguna</button>
              </span>
            )}
          </div>
          {maquinas.length === 0 ? (
            <p className="text-sm text-gray-400">Este cliente no tiene máquinas registradas.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {maquinas.map((m, i) => {
                const id = String(m.id_maquina);
                return (
                  <label key={`${id}-${i}`} htmlFor={`rol-maquina-${id}-${i}`} className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 cursor-pointer min-w-0">
                    <input
                      id={`rol-maquina-${id}-${i}`}
                      type="checkbox"
                      checked={maquinasRol.has(id)}
                      onChange={() => alternarMaquina(id)}
                      className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="min-w-0">
                      <span className="block font-mono text-xs text-gray-800 truncate">{id}</span>
                      <span className="block text-xs text-gray-500 truncate">{m.descripcion_maq || 'Sin descripción'}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </section>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      </form>
    </Modal>
  );
};

export default FormularioRolPuntoV;
